import React, { useState } from "react";
import PublicNavbar from "../../components/public/PublicNavbar";
import InquiryForm from "../../components/public/InquiryForm";
import { Link } from "react-router-dom";
import {
    FaIndustry, FaCogs, FaDraftingCompass, FaCheckCircle
} from "react-icons/fa";

const WhatWeDo = () => {
    const [showInquiry, setShowInquiry] = useState(false);

    return (
        <div className="min-h-screen bg-[#FFFFFF] text-[#263746] font-sans">
            <PublicNavbar />

            {/* Hero Header */}
            <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] py-14 px-6 text-center">
                <div className="max-w-4xl mx-auto flex flex-col items-center">
                    <img
                        src="/logo.png"
                        alt="LUMIGO Projection Solutions"
                        className="h-12 sm:h-14 w-auto object-contain mb-4 drop-shadow-xs"
                    />
                    <span className="text-[#E3B629] text-xs font-black tracking-[0.25em] uppercase block mb-2">
                        Optical Systems & Custom Fabrication
                    </span>
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-[#263746] leading-tight">
                        What We Do at LUMIGO
                    </h1>
                    <p className="text-[#687482] text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                        LUMIGO specializes in end-to-end commercial gobo projection systems — from industrial safety hardware and facade logo projectors to precision-etched glass gobos and optical site calculations.
                    </p>
                </div>
            </div>

            {/* Core Pillars */}
            <div className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-8 hover:border-[#E3B629] transition-all space-y-4 shadow-sm">
                        <div className="w-12 h-12 rounded-xl bg-[#F7F5EF] text-[#E3B629] border border-[#E5E7EB] flex items-center justify-center text-2xl">
                            <FaIndustry />
                        </div>
                        <h3 className="text-xl font-bold text-[#263746]">Commercial Gobo Projectors</h3>
                        <p className="text-[#687482] text-xs leading-relaxed">
                            We supply IP65-rated outdoor and high-lumen indoor projectors ranging from 50W to 300W with OSRAM optical engines designed for continuous duty cycles in harsh environments.
                        </p>
                        <ul className="text-xs text-[#687482] space-y-1.5 pt-2">
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> Die-cast weather-sealed housings</li>
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> 3m to 25m+ optical throw lenses</li>
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> High thermal efficiency heat sinking</li>
                        </ul>
                    </div>

                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-8 hover:border-[#E3B629] transition-all space-y-4 shadow-sm">
                        <div className="w-12 h-12 rounded-xl bg-[#F7F5EF] text-[#E3B629] border border-[#E5E7EB] flex items-center justify-center text-2xl">
                            <FaDraftingCompass />
                        </div>
                        <h3 className="text-xl font-bold text-[#263746]">Custom Laser-Etched Gobos</h3>
                        <p className="text-[#687482] text-xs leading-relaxed">
                            We manufacture custom monochrome and full-colour dichroic glass gobos from vector logos, CAD architectural layouts, and festival designs with zero degradation or burn-out.
                        </p>
                        <ul className="text-xs text-[#687482] space-y-1.5 pt-2">
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> High-temp quartz & dichroic glass</li>
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> Single static and 4-logo rotation options</li>
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> Extreme resolution down to 0.05mm lines</li>
                        </ul>
                    </div>

                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-8 hover:border-[#E3B629] transition-all space-y-4 shadow-sm">
                        <div className="w-12 h-12 rounded-xl bg-[#F7F5EF] text-[#E3B629] border border-[#E5E7EB] flex items-center justify-center text-2xl">
                            <FaCogs />
                        </div>
                        <h3 className="text-xl font-bold text-[#263746]">Optical Engineering & Advisory</h3>
                        <p className="text-[#687482] text-xs leading-relaxed">
                            We assist architects, plant heads, and commercial contractors with lux calculations, throw distance sizing, focal angle selection, and mounting bracket selection.
                        </p>
                        <ul className="text-xs text-[#687482] space-y-1.5 pt-2">
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> Ambient lux analysis and wattage matching</li>
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> Projection image diameter calculations</li>
                            <li className="flex items-center gap-2"><FaCheckCircle className="text-[#E3B629] text-[10px]" /> Direct engineer-to-client consultation</li>
                        </ul>
                    </div>
                </div>

                {/* 4-Step Process Section */}
                <div className="border border-[#E5E7EB] bg-[#F7F5EF] rounded-2xl p-8 sm:p-12 mb-20 shadow-sm">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <span className="text-[#E3B629] text-xs font-black tracking-[0.2em] uppercase">Structured Workflow</span>
                        <h2 className="text-3xl font-extrabold text-[#263746] mt-2">How Our Enquiry-Based Ordering Works</h2>
                        <p className="text-[#687482] text-xs mt-2">
                            We operate on a transparent B2B quotation model ensuring exact technical validation before payment or dispatch.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-[#FFFFFF] p-6 rounded-xl border border-[#E5E7EB] relative shadow-sm">
                            <span className="text-3xl font-extrabold text-[#E3B629] font-mono block mb-2">01</span>
                            <h4 className="font-bold text-[#263746] text-sm mb-2">Submit Enquiry</h4>
                            <p className="text-[#687482] text-xs leading-relaxed">
                                Choose your projector model or submit custom requirements with throw distance and logo artwork.
                            </p>
                        </div>
                        <div className="bg-[#FFFFFF] p-6 rounded-xl border border-[#E5E7EB] relative shadow-sm">
                            <span className="text-3xl font-extrabold text-[#E3B629] font-mono block mb-2">02</span>
                            <h4 className="font-bold text-[#263746] text-sm mb-2">Technical Validation</h4>
                            <p className="text-[#687482] text-xs leading-relaxed">
                                Our engineers confirm focal length, wattage suitability, and glass gobo artwork proofing.
                            </p>
                        </div>
                        <div className="bg-[#FFFFFF] p-6 rounded-xl border border-[#E5E7EB] relative shadow-sm">
                            <span className="text-3xl font-extrabold text-[#E3B629] font-mono block mb-2">03</span>
                            <h4 className="font-bold text-[#263746] text-sm mb-2">Commercial Quote</h4>
                            <p className="text-[#687482] text-xs leading-relaxed">
                                We issue a formal commercial quotation including GST, lead time, and offline payment instructions.
                            </p>
                        </div>
                        <div className="bg-[#FFFFFF] p-6 rounded-xl border border-[#E5E7EB] relative shadow-sm">
                            <span className="text-3xl font-extrabold text-[#E3B629] font-mono block mb-2">04</span>
                            <h4 className="font-bold text-[#263746] text-sm mb-2">Dispatch & Support</h4>
                            <p className="text-[#687482] text-xs leading-relaxed">
                                Equipment is tested, carefully packaged, dispatched, and supported with mounting guidance.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Call to action */}
                <div className="text-center py-8">
                    <button
                        onClick={() => setShowInquiry(true)}
                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition shadow-sm"
                    >
                        Request Project Quotation
                    </button>
                    <div className="mt-4">
                        <Link to="/products" className="text-[#687482] hover:text-[#263746] text-xs font-semibold underline">
                            Or browse our 10 commercial projector models
                        </Link>
                    </div>
                </div>
            </div>

            {showInquiry && <InquiryForm onClose={() => setShowInquiry(false)} />}
        </div>
    );
};

export default WhatWeDo;
